import type {
  EntityFieldItemApplier,
  EntityFieldItemOperation,
  EntityFieldValuesDeltaApplier,
  EntityFieldItemsUpdater,
} from '@github-ui/optimistic-connection'
import {useEntityFieldsBridge} from '@github-ui/optimistic-connection/connectionMutationBridge'
import type {SettleTarget} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import type {SetFieldsMapper} from '@github-ui/optimistic-connection/pendingOperations'
import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'

import type {IssuesDashboardEnrichedDataPayload} from '../types/routes-types'

const ID_PATH = ['id']
const LEGACY_GLOBAL_ID_PATH = ['globalId']

type EnrichedRow = IssuesDashboardEnrichedDataPayload['issues'][number]
type IssueRow = Extract<EnrichedRow, {itemType: 'issue'}>
type OptimisticRow = {
  itemType: 'issue' | 'pull_request'
  id: number | string
  globalId?: string
}
type IssueLabel = IssueRow['labels'][number]
type ProjectedLabel = Omit<IssueLabel, 'id'> & Required<Pick<IssueLabel, 'color' | 'nameHtml'>>
type ProjectedAssignee = Pick<IssueRow['assignees'][number], 'login' | 'avatarUrl'>

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function isProjectedLabel(value: unknown): value is ProjectedLabel {
  return (
    isRecord(value) &&
    typeof value.name === 'string' &&
    typeof value.nameHtml === 'string' &&
    typeof value.color === 'string' &&
    (value.description === undefined || typeof value.description === 'string')
  )
}

function mapLabels(labels: unknown[]): ProjectedLabel[] | undefined {
  if (!labels.every(isProjectedLabel)) return undefined

  return labels.map(label => ({
    name: label.name,
    nameHtml: label.nameHtml,
    color: label.color,
    ...(label.description === undefined ? {} : {description: label.description}),
  }))
}

function isProjectedAssignee(value: unknown): value is ProjectedAssignee {
  return isRecord(value) && typeof value.login === 'string' && typeof value.avatarUrl === 'string'
}

function mapAssignedActors(assignedActors: unknown): ProjectedAssignee[] | undefined {
  if (!isRecord(assignedActors) || !Array.isArray(assignedActors.nodes)) return undefined
  if (!assignedActors.nodes.every(isProjectedAssignee)) return undefined

  return assignedActors.nodes.map(assignee => ({login: assignee.login, avatarUrl: assignee.avatarUrl}))
}

function mapIssueFieldValue(
  value: Readonly<Record<string, unknown>>,
): NonNullable<IssueRow['issueFieldValues']>[number] | undefined {
  const field = value.field
  if (!isRecord(field) || typeof field.name !== 'string') return undefined
  const fieldName = field.name

  switch (value.__typename) {
    case 'IssueFieldTextValue':
      return typeof value.value === 'string' || value.value === null
        ? {fieldName, dataType: 'text', value: value.value}
        : undefined
    case 'IssueFieldSingleSelectValue':
      return typeof value.name === 'string' || value.name === null
        ? {fieldName, dataType: 'single_select', value: value.name}
        : undefined
    case 'IssueFieldMultiSelectValue': {
      if (!Array.isArray(value.options)) return undefined
      const options = value.options.map(option => (isRecord(option) ? option.name : undefined))
      return options.every((name): name is string => typeof name === 'string')
        ? {fieldName, dataType: 'multi_select', options}
        : undefined
    }
    case 'IssueFieldDateValue':
      return typeof value.value === 'string' || value.value === null
        ? {fieldName, dataType: 'date', value: value.value}
        : undefined
    case 'IssueFieldNumberValue':
      return typeof value.numberValue === 'number' || value.numberValue === null
        ? {fieldName, dataType: 'number', numberValue: value.numberValue}
        : undefined
    default:
      return undefined
  }
}

function fieldNameFromOperation(operation: EntityFieldItemOperation): string | undefined {
  const field = operation.item.field
  return isRecord(field) && typeof field.name === 'string' ? field.name : undefined
}

export const applyIssueEntityFieldItemToIssueRow: EntityFieldItemApplier = (entity, operation) => {
  if (operation.field !== 'issueFieldValues') return
  const fieldName = fieldNameFromOperation(operation)
  if (!fieldName) return

  if (operation.type === 'removeEntityFieldItem') {
    if (!Array.isArray(entity.issueFieldValues)) return
    const values = entity.issueFieldValues as NonNullable<IssueRow['issueFieldValues']>
    const index = values.findIndex(value => value.fieldName === fieldName)
    if (index !== -1) values.splice(index, 1)
    return
  }

  const item = mapIssueFieldValue(operation.item)
  if (!item) return
  const values = Array.isArray(entity.issueFieldValues)
    ? (entity.issueFieldValues as NonNullable<IssueRow['issueFieldValues']>)
    : (entity.issueFieldValues = [])
  const index = values.findIndex(value => value.fieldName === fieldName)
  if (index === -1) values.push(item)
  else values[index] = item
}

export const mapIssueViewerFieldsToIssueRow: SetFieldsMapper = operationFields => {
  const fields: Record<string, unknown> = {}
  if (typeof operationFields.title === 'string') fields.title = operationFields.title
  if (typeof operationFields.titleHTML === 'string') fields.titleHtml = operationFields.titleHTML
  if (typeof operationFields.state === 'string') fields.state = operationFields.state
  if (operationFields.stateReason === null || typeof operationFields.stateReason === 'string') {
    fields.stateReason = operationFields.stateReason
  }
  if (typeof operationFields.closed === 'boolean') fields.closed = operationFields.closed
  if (operationFields.closedAt === null || typeof operationFields.closedAt === 'string') {
    fields.closedAt = operationFields.closedAt
  }
  if (typeof operationFields.comments === 'number') fields.commentCount = operationFields.comments
  if (typeof operationFields.blockedBy === 'number') fields.blockedByCount = operationFields.blockedBy
  const issueDependenciesSummary = operationFields.issueDependenciesSummary
  if (isRecord(issueDependenciesSummary) && typeof issueDependenciesSummary.blockedBy === 'number') {
    fields.blockedByCount = issueDependenciesSummary.blockedBy
  }
  const subIssuesSummary = operationFields.subIssuesSummary
  if (
    isRecord(subIssuesSummary) &&
    typeof subIssuesSummary.total === 'number' &&
    typeof subIssuesSummary.completed === 'number'
  ) {
    fields.subIssuesSummary = {
      total: subIssuesSummary.total,
      completed: subIssuesSummary.completed,
      percentCompleted:
        subIssuesSummary.total === 0 ? 0 : Math.floor((subIssuesSummary.completed / subIssuesSummary.total) * 100),
    }
  }
  const issueType = operationFields.issueType
  if (issueType === null) {
    fields.issueType = null
  } else if (
    issueType &&
    typeof issueType === 'object' &&
    !Array.isArray(issueType) &&
    typeof (issueType as Record<string, unknown>).name === 'string' &&
    typeof (issueType as Record<string, unknown>).color === 'string'
  ) {
    fields.issueType = {
      name: (issueType as Record<string, unknown>).name,
      color: (issueType as Record<string, unknown>).color,
    }
  }
  const milestone = operationFields.milestone
  if (milestone === null) {
    fields.milestone = null
  } else if (isRecord(milestone) && typeof milestone.title === 'string' && typeof milestone.url === 'string') {
    fields.milestone = {
      title: milestone.title,
      url: milestone.url,
    }
  }
  if (Array.isArray(operationFields.labels)) {
    const labels = mapLabels(operationFields.labels)
    if (labels) fields.labels = labels
  }
  const assignees = mapAssignedActors(operationFields.assignedActors)
  if (assignees) fields.assignees = assignees
  return Object.keys(fields).length > 0 ? fields : undefined
}

export function mapIssueEntityFieldToIssueRow(field: string): string | undefined {
  if (field === 'comments') return 'commentCount'
  if (field === 'blockedBy') return 'blockedByCount'
  return undefined
}

export const applyIssueEntityFieldValuesDeltaToIssueRow: EntityFieldValuesDeltaApplier = (entity, operation) => {
  if (operation.field === 'issueDependenciesSummary') {
    const blockedByDelta = operation.deltas.blockedBy
    if (
      typeof entity.blockedByCount !== 'number' ||
      typeof blockedByDelta !== 'number' ||
      !Number.isFinite(blockedByDelta)
    )
      return false
    entity.blockedByCount = Math.max(0, entity.blockedByCount + blockedByDelta)
    return true
  }
  if (operation.field !== 'subIssuesSummary' || !isRecord(entity.subIssuesSummary)) return false
  const summary = entity.subIssuesSummary as Record<string, unknown>
  if (typeof summary.total !== 'number' || typeof summary.completed !== 'number') return false
  const totalDelta = operation.deltas.total ?? 0
  const completedDelta = operation.deltas.completed ?? 0
  if (!Number.isFinite(totalDelta) || !Number.isFinite(completedDelta)) return false

  const total = Math.max(0, summary.total + totalDelta)
  const completed = Math.min(total, Math.max(0, summary.completed + completedDelta))
  summary.total = total
  summary.completed = completed
  summary.percentCompleted = total === 0 ? 0 : Math.floor((completed / total) * 100)
  return true
}

export const updateIssueEntityFieldItemsInIssueRow: EntityFieldItemsUpdater = (entity, operation) => {
  if (operation.field !== 'linkedPullRequests') return false
  if (typeof entity.linkedPullRequestCount !== 'number' || !Array.isArray(entity.linkedPullRequests)) return false

  const removed = new Set(
    operation.removeKeys.map(linkedPullRequestKey).filter((key): key is string => key !== undefined),
  )
  const retained = (entity.linkedPullRequests as Array<Record<string, unknown>>).filter(item => {
    const key = typeof item.permalink === 'string' ? linkedPullRequestKey(item.permalink) : undefined
    return key === undefined || !removed.has(key)
  })
  for (const {key, item} of operation.upserts.toReversed()) {
    const summary = toIssueLinkedPullRequestSummary(item)
    if (!summary) continue
    const normalizedKey = linkedPullRequestKey(key)
    const existing = retained.findIndex(
      candidate =>
        typeof candidate.permalink === 'string' && linkedPullRequestKey(candidate.permalink) === normalizedKey,
    )
    if (existing !== -1) retained.splice(existing, 1)
    retained.unshift(summary)
  }

  entity.linkedPullRequests = retained.slice(0, 10)
  entity.linkedPullRequestCount = Math.max(0, entity.linkedPullRequestCount + operation.countDelta)
  return true
}

function linkedPullRequestKey(value: string): string | undefined {
  try {
    return new URL(value, 'https://github.com').pathname.replace(/\/$/, '')
  } catch {
    return undefined
  }
}

function toIssueLinkedPullRequestSummary(item: Record<string, unknown>): Record<string, unknown> | undefined {
  const repository = isRecord(item.repository) ? item.repository : undefined
  const permalink = typeof item.url === 'string' ? linkedPullRequestKey(item.url) : undefined
  if (!permalink || !repository || typeof repository.nameWithOwner !== 'string') return undefined
  return {
    title: typeof item.title === 'string' ? item.title : null,
    number: typeof item.number === 'number' ? item.number : null,
    permalink,
    repoNameWithOwner: repository.nameWithOwner,
    state: typeof item.state === 'string' ? item.state : null,
    isDraft: typeof item.isDraft === 'boolean' ? item.isDraft : null,
  }
}

/**
 * Mirrors Relay-published issue field edits into the mutation cache. Settlement is not here: the
 * document that renders the rows names the caches it holds, via `useOptimisticDashboardRows`.
 */
export function useDashboardIssueMutationBridge(): void {
  useEntityFieldsBridge()
}

export function useOptimisticDashboardRows<T extends OptimisticRow>({
  base,
  targets,
}: {
  base: T[]
  /** The caches holding these rows: the list query, and the per-row node cache. */
  targets?: SettleTarget[]
}): T[] {
  const identityPath = base.some(row => row.itemType === 'issue' && typeof row.id === 'string')
    ? ID_PATH
    : LEGACY_GLOBAL_ID_PATH

  return useOptimisticData(base, {
    enabled: true,
    targets,
    identityPath,
    mapFields: mapIssueViewerFieldsToIssueRow,
    mapEntityField: mapIssueEntityFieldToIssueRow,
    applyEntityFieldItem: applyIssueEntityFieldItemToIssueRow,
    applyEntityFieldValuesDelta: applyIssueEntityFieldValuesDeltaToIssueRow,
    updateEntityFieldItems: updateIssueEntityFieldItemsInIssueRow,
  })
}
